import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import {
  AccountBalanceWallet as CreditIcon,
  CalendarToday as CalendarIcon,
  Notes as NotesIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useAddCreditBatchOps } from "@taxassist/api-client";
import type { FirmResponse } from "@taxassist/api-client";

interface AddCreditBatchDialogProps {
  open: boolean;
  onClose: () => void;
  firm: FirmResponse;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

interface FormData {
  credits: string;
  expiryDate: Date | null;
  invoiceId: string;
}

interface FormErrors {
  credits?: string;
  expiryDate?: string;
  invoiceId?: string;
}

/**
 * AddCreditBatchDialog provides a form for admins to add credit batches to firms.
 * Includes validation, error handling, and success notifications.
 */
export function AddCreditBatchDialog({
  open,
  onClose,
  firm,
  onSuccess,
  onError,
}: AddCreditBatchDialogProps) {
  const [formData, setFormData] = useState<FormData>({
    credits: "",
    expiryDate: null,
    invoiceId: "",
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Credit batch addition mutation
  const addCreditMutation = useAddCreditBatchOps({
    mutation: {
      onSuccess: (response) => {
        const message = `Successfully added ${response.credits} credits to ${firm.name}. Batch ID: ${response.batchId}`;
        onSuccess?.(message);
        handleClose();
      },
      onError: (error) => {
        const errorMessage =
          error.message || "Failed to add credit batch. Please try again.";
        setSubmitError(errorMessage);
        onError?.(errorMessage);
      },
    },
  });

  const handleClose = useCallback(() => {
    setFormData({ credits: "", expiryDate: null, invoiceId: "" });
    setFormErrors({});
    setSubmitError(null);
    onClose();
  }, [onClose]);

  const validateForm = useCallback((): boolean => {
    const errors: FormErrors = {};

    // Validate credits
    const creditsNum = parseInt(formData.credits, 10);
    if (!formData.credits.trim()) {
      errors.credits = "Credits are required";
    } else if (isNaN(creditsNum) || creditsNum < 1) {
      errors.credits = "Credits must be a positive number";
    } else if (creditsNum > 10000) {
      errors.credits = "Credits cannot exceed 10,000";
    }

    // Validate expiry date
    if (!formData.expiryDate) {
      errors.expiryDate = "Expiry date is required";
    } else {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(formData.expiryDate);
      selectedDate.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        errors.expiryDate = "Expiry date must be today or in the future";
      }

      // Check if expiry date is more than 3 years in the future
      const maxDate = new Date();
      maxDate.setFullYear(maxDate.getFullYear() + 3);
      if (selectedDate > maxDate) {
        errors.expiryDate =
          "Expiry date cannot be more than 3 years in the future";
      }
    }

    // Validate invoiceId
    if (!formData.invoiceId.trim()) {
      errors.invoiceId = "Invoice ID is required";
    } else if (formData.invoiceId.trim().length > 100) {
      errors.invoiceId = "Invoice ID cannot exceed 100 characters";
    } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.invoiceId.trim())) {
      errors.invoiceId =
        "Invoice ID can only contain letters, numbers, hyphens, and underscores";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitError(null);

    // Format date as YYYY-MM-DD in local timezone (not UTC)
    const year = formData.expiryDate!.getFullYear();
    const month = String(formData.expiryDate!.getMonth() + 1).padStart(2, "0");
    const day = String(formData.expiryDate!.getDate()).padStart(2, "0");
    const expiryDateString = `${year}-${month}-${day}`;

    addCreditMutation.mutate({
      firmId: firm.firmId,
      data: {
        credits: parseInt(formData.credits, 10),
        expiryDate: expiryDateString,
        invoiceId: formData.invoiceId.trim(),
      },
    });
  }, [formData, firm.firmId, validateForm, addCreditMutation]);

  const handleInputChange = useCallback(
    (field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));

      // Clear error when user starts typing
      if (formErrors[field]) {
        setFormErrors((prev) => ({
          ...prev,
          [field]: undefined,
        }));
      }
    },
    [formErrors],
  );

  const handleDateChange = useCallback(
    (date: Date | null) => {
      setFormData((prev) => ({
        ...prev,
        expiryDate: date,
      }));

      // Clear error when user selects date
      if (formErrors.expiryDate) {
        setFormErrors((prev) => ({
          ...prev,
          expiryDate: undefined,
        }));
      }
    },
    [formErrors],
  );

  const isSubmitting = addCreditMutation.isPending;
  const canSubmit =
    !isSubmitting &&
    formData.credits &&
    formData.expiryDate &&
    formData.invoiceId;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { minHeight: 500 },
      }}
    >
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}
      >
        <Box>
          <Typography variant="h6" component="div">
            Add Credit Batch
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {firm.name}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 2 }}>
          {/* Credits Input */}
          <TextField
            label="Number of Credits"
            type="number"
            value={formData.credits}
            onChange={handleInputChange("credits")}
            error={!!formErrors.credits}
            disabled={isSubmitting}
            fullWidth
            required
            InputProps={{
              inputProps: {
                min: 1,
                max: 10000,
                step: 1,
              },
            }}
          />

          {/* Expiry Date */}
          <DatePicker
            label="Expiry Date"
            value={formData.expiryDate}
            onChange={handleDateChange}
            disabled={isSubmitting}
            minDate={new Date()} // Today
            maxDate={new Date(Date.now() + 3 * 365 * 24 * 60 * 60 * 1000)} // 3 years from now
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                error: !!formErrors.expiryDate,
              },
            }}
          />

          {/* Invoice ID */}
          <TextField
            label="Invoice ID"
            value={formData.invoiceId}
            onChange={handleInputChange("invoiceId")}
            error={!!formErrors.invoiceId}
            disabled={isSubmitting}
            fullWidth
            required
          />

          {/* Summary */}
          {canSubmit && (
            <Box sx={{ p: 2, backgroundColor: "grey.50", borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Summary
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Adding <strong>{formData.credits} credits</strong> to{" "}
                <strong>{firm.name}</strong>, expiring on{" "}
                <strong>{formData.expiryDate?.toLocaleDateString()}</strong>
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button onClick={handleClose} disabled={isSubmitting} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          variant="contained"
          startIcon={
            isSubmitting ? <CircularProgress size={16} /> : <CreditIcon />
          }
        >
          {isSubmitting ? "Adding Credits..." : "Add Credits"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
